import { KeyboardArrowRight } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useGetTitrationForProductQuery } from "src/api/services/titrationApi";
import { OneTimeProduct, ProductCategories } from "src/api/types";
import { theme } from "src/assets/theme";
import { Paths } from "src/constants/paths";
import { useIsMobile } from "src/hooks/useIsMobile";
import WhiteCard from "src/shared/components/WhiteCard";
import { mapPhaseToMonths } from "./utils/mapPhaseToMonths";
import { skipToken } from "@reduxjs/toolkit/query";
import { useRecommendedDosage } from "src/context/RecommendedDosageProvider";
import { useLatestOrder } from "src/context/LatestOrder";
import { useGetPatientCouponsQuery } from "src/api/services/patientCouponsApi";

interface CardProps {
  product: OneTimeProduct;
}

const mapProductToBgColor: Record<number, string> = {
  1: "#00B5B81A",
  2: "#00B5B833",
  3: "#00B5B84D",
  4: "#00B5B866",
  5: "#00B5B880",
  6: "#00B5B899",
};

const ProductCard = ({ product }: CardProps) => {
  const isMobile = useIsMobile();
  const recommended = useRecommendedDosage();
  const latestOrder = useLatestOrder((o) => o);
  const { data: nextDose } = useGetTitrationForProductQuery(
    recommended != null &&
      latestOrder?.product_category.name === ProductCategories.WeightLoss
      ? latestOrder.product.slug
      : skipToken,
  );
  const { data: coupons = [] } = useGetPatientCouponsQuery({
    product_slug: product.product.slug,
    redeemed: false,
  });

  const { t } = useTranslation();
  const component = "oneTimeProducts.productCard";
  const navigate = useNavigate();

  const recommendedDose = useMemo(() => {
    if (nextDose == null) return;

    if (nextDose.max_dose == null && nextDose.min_dose == null) return;

    const doseRange = `${nextDose.min_dose} - ${nextDose.max_dose}`;

    return (
      product.sort_order === nextDose.tier && product.dosage.includes(doseRange)
    );
  }, [nextDose, product.dosage, product.sort_order]);

  const discount = useMemo(() => {
    return coupons.reduce((acc, c) => (acc += c.amount), 0);
  }, [coupons]);

  const price = useMemo(() => {
    if (product.price.number_of_months === 3) {
      const quarterlyDiscount = discount / 3;
      const quarterlyDiscountedPrice =
        (product.price.monthly_price ?? 0) - quarterlyDiscount;

      const amountLimitMonthly = (product.price.amount_limit ?? 0) / 3;

      return t(`${component}.quarterly-price`, "{{price}}/month", {
        price: Math.max(
          Number(quarterlyDiscountedPrice.toFixed()),
          amountLimitMonthly ?? 0,
        ),
      });
    }

    const discountedPrice = product.price.amount - discount;

    return Math.max(
      Number(discountedPrice.toFixed()),
      product.price.amount_limit ?? 0,
    );
  }, [
    discount,
    product.price.amount,
    product.price.amount_limit,
    product.price.monthly_price,
    product.price.number_of_months,
    t,
  ]);

  const percentSavings = useMemo(() => {
    if (discount === 0) {
      return product.savings;
    }

    const basePercent = parseInt(product.savings);
    const couponPercent = (discount / product.price.amount) * 100;

    return `${Math.round(basePercent + couponPercent)}%`;
  }, [discount, product.price.amount, product.savings]);

  const handleSelect = useCallback(
    (ot: OneTimeProduct) => {
      const maxDose = ot.dosage.split(" - ")[1];

      const params = createSearchParams({
        dosage: maxDose,
        id: ot.price.stripe_id,
        amount: String(ot.price.amount),
      }).toString();

      navigate({
        pathname: `/${Paths.ADD_ONS}/${ot.product.slug}`,
        search: `${params}`,
      });
    },
    [navigate],
  );

  const monthText = useMemo(() => {
    if (product.price.number_of_months === 1) return "";

    const months =
      mapPhaseToMonths[`${product.product.slug}-${product.sort_order}`];

    return t(
      `${component}.${product.product.slug}.${product.sort_order}.months`,
      months,
    );
  }, [
    product.sort_order,
    product.price.number_of_months,
    product.product.slug,
    t,
  ]);

  return (
    <WhiteCard
      onClick={() => handleSelect(product)}
      direction={isMobile ? "column" : "row"}
      justifyContent="space-between"
      sx={{
        cursor: "pointer",
        backgroundColor: recommendedDose
          ? "#FFF20033"
          : (mapProductToBgColor[product.sort_order] ?? "#00B5B81A"),
      }}
    >
      <Stack gap={0.5}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction={"row"} spacing={0.5}>
            <Typography fontWeight={700}>
              {t(
                `${component}.${product.product.slug}.${product.dosage}.${product.sort_order}.label`,
                product.label,
              )}
            </Typography>
            <Typography>{monthText}</Typography>
          </Stack>
          {isMobile ? (
            <Typography fontWeight={600} color={theme.palette.secondary.main}>
              {t(`save {{savings}}`, { savings: percentSavings })}
            </Typography>
          ) : null}
        </Stack>
        <Typography variant="body2">
          {product.dosage} -{" "}
          <span style={{ fontStyle: "italic" }}>
            {t(
              `${component}.${product.product.slug}.${product.dosage}.${product.sort_order}.description`,
              product.description,
            )}
          </span>
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" gap={2} flexShrink={0}>
        <Box
          sx={{
            width: 170,
            display: { xs: "flex", sm: "flex" },
            alignItems: "center",
          }}
        >
          {recommendedDose ? (
            <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
              <Typography
                variant={isMobile ? "body2" : "body1"}
                fontWeight={700}
              >
                {t(`${component}.recommended`, "Recommended")}
              </Typography>
              <KeyboardArrowRight />
            </Stack>
          ) : null}
        </Box>
        {isMobile ? null : (
          <Typography
            whiteSpace={"nowrap"}
            fontWeight={600}
            color={theme.palette.secondary.main}
          >
            {t(`save {{savings}}`, { savings: percentSavings })}
          </Typography>
        )}
        <Button
          fullWidth={isMobile ? true : false}
          sx={{ backgroundColor: "#027B8E", height: 30, minWidth: 120 }}
          onClick={() => handleSelect(product)}
        >{`$${price}`}</Button>
      </Stack>
    </WhiteCard>
  );
};

export default ProductCard;
